// 🫂 The care cards' client rules (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §5.12,
// mockup room-toolkit.html §7-§8). Two separate lanes, one file:
//
// 1. THE CHECK-IN ("Heavy stuff tonight."): a PRIVATE card for one viewer in a
//    heavy room (status `heavy_room: true`, backend heavyRoom.js), after 20+
//    minutes in the room THIS visit, at most once per space per day per device.
//    "Don't ask again" retires it for that room on this device, forever. Nothing
//    the viewer taps ever leaves the device: no request, no prompt, no room card.
//    (What "Something lighter" does is the page's call, through onLighter.)
//
// 2. THE CRISIS CARD: shown only to the sender whose own line the server marked
//    (`care: 'crisis'` on their interjection response, backend careCrisis.js),
//    once per session (this tab).
//
// Storage is a per-viewer convenience that can be missing, blocked or full
// (roomHint.js's rule): every read/write is guarded, nothing here throws, and a
// device that cannot remember just asks again on a later visit (never twice in
// one visit: the card's own state holds that).

export const CHECKIN_AFTER_MS = 20 * 60 * 1000;
export const MUTE_MS = 10 * 60 * 1000;
export const CHECKIN_KEY_PREFIX = 'care_checkin_v1:';
export const CRISIS_SESSION_KEY = 'care_crisis_shown_v1';

const keyFor = (roomId) => `${CHECKIN_KEY_PREFIX}${roomId}`;

function read(store, roomId) {
  try {
    const raw = store && roomId ? store.getItem(keyFor(roomId)) : null;
    const v = raw ? JSON.parse(raw) : null;
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
  } catch (e) { return {}; }
}
function write(store, roomId, v) {
  try { if (store && roomId) store.setItem(keyFor(roomId), JSON.stringify(v)); } catch (e) { /* dropped on purpose */ }
}

/** The device's own calendar day ('YYYY-MM-DD', local time): "once a day". */
export function localDay(nowMs) {
  const d = new Date(nowMs);
  if (!Number.isFinite(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** { asked: 'YYYY-MM-DD' | undefined, never: ms | undefined } for a room. */
export function checkInState(store, roomId) {
  return read(store, roomId);
}

/** ms until this visit reaches the 20-minute mark (0 = now), or null when unknown. */
export function msUntilCheckIn({ enteredAt, now }) {
  const at = Number(enteredAt);
  const t = Number(now);
  if (!Number.isFinite(at) || !Number.isFinite(t) || at <= 0) return null;
  return Math.max(0, at + CHECKIN_AFTER_MS - t);
}

/** Should the check-in show right now on this device? */
export function checkInDue({ roomId, heavy, enteredAt, now, store }) {
  if (heavy !== true || !roomId) return false;
  const wait = msUntilCheckIn({ enteredAt, now });
  if (wait === null || wait > 0) return false;
  const v = read(store, roomId);
  if (v.never) return false;
  return v.asked !== localDay(now);
}

/** The card is on screen: that is today's one ask for this room. */
export function markCheckInAsked(store, roomId, nowMs) {
  const v = read(store, roomId);
  write(store, roomId, { ...v, asked: localDay(nowMs) });
}

/** "Don't ask again": never again for this room, on this device. */
export function retireCheckIn(store, roomId, nowMs) {
  const v = read(store, roomId);
  write(store, roomId, { ...v, never: Number(nowMs) || 1 });
}

/** Undo a "Don't ask again" (the card's Undo). Today's ask still counts. */
export function unretireCheckIn(store, roomId) {
  const v = read(store, roomId);
  delete v.never;
  write(store, roomId, v);
}

/** The browser store, or null where there is none (SSR, blocked storage). */
export function careStore() {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch (e) { return null; }
}

// ── the crisis card ─────────────────────────────────────────────────────────

/** The session store (this tab), or null. */
export function careSessionStore() {
  try { return typeof window !== 'undefined' ? window.sessionStorage : null; } catch (e) { return null; }
}

// A blocked sessionStorage still gets "once per page load".
let crisisShownInMemory = false;

/** Did the server mark THIS response for the sender's crisis card? */
export function crisisFromResponse(resp) {
  return !!(resp && typeof resp === 'object' && resp.care === 'crisis');
}

export function crisisShownThisSession(session) {
  if (crisisShownInMemory) return true;
  try { return !!(session && session.getItem(CRISIS_SESSION_KEY)); } catch (e) { return false; }
}

export function markCrisisShown(session, nowMs = Date.now()) {
  crisisShownInMemory = true;
  try { if (session) session.setItem(CRISIS_SESSION_KEY, String(nowMs)); } catch (e) { /* in memory only */ }
}

/**
 * The page's one call after a send: true = open the crisis card now (and it is
 * marked shown for this session). false for every other response.
 */
export function takeCrisisCard(resp, session = careSessionStore(), nowMs = Date.now()) {
  if (!crisisFromResponse(resp) || crisisShownThisSession(session)) return false;
  markCrisisShown(session, nowMs);
  return true;
}

/** Tests only: forget the in-memory once-gate. */
export function _resetCrisisMemoryForTests() { crisisShownInMemory = false; }
